// Created by: Imran Khan July 2026
import React, { useCallback, useEffect, useState } from "react";
import { Col, Container, Row, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import fuziElevatorsApi from "../../../api/fuziElevatorsApi";
import { formatDate, getUserDetails } from "../../../utils/helper";
import PERMISSIONS from "../../../constants/permissions";
import { ROUTES } from "../../../constants/webRouting";
import { buildDashboardFilterSearch } from "../../../utils/dashboardFilterParams";
import DashboardFilterBar from "./DashboardFilterBar";
import KpiCardRow from "./KpiCardRow";
import { RevenueSummaryCards, CollectionSummaryCards, DetailedCollectionSummaryCard } from "./SummaryCard";
import DashboardDonutChart from "./DashboardDonutChart";
import DashboardLineChart from "./DashboardLineChart";
import DashboardBarChart from "./DashboardBarChart";
import {
  DashboardTable,
  StatusList,
  MetricGrid,
  ProgressPanel,
  QuickSummary,
} from "./DashboardTables";
import {
  getCurrentFinancialYear,
  formatCurrency,
  buildDashboardQueryParams,
} from "./financialYearUtils";
import "./FinancialYearDashboard.css";

const ENQUIRY_COLUMNS = [
  { key: "type", label: "Type" },
  { key: "new", label: "New" },
  { key: "inProgress", label: "In Progress" },
  { key: "converted", label: "Converted" },
  { key: "closed", label: "Closed" },
  { key: "total", label: "Total" },
];

export default function FinancialYearDashboard() {
  const navigate = useNavigate();
  const userInfo = getUserDetails();
  const permissionNames = (userInfo?.permissions || [])
    .map((p) => p?.name)
    .filter(Boolean);
  const canView =
    userInfo?.userrole === "SUPER_ADMIN" ||
    [PERMISSIONS.VIEW_ALL, PERMISSIONS.VIEW_DASHBOARD].some((permission) =>
      permissionNames.includes(permission),
    );

  const defaultFY = getCurrentFinancialYear();
  const [financialYears, setFinancialYears] = useState([defaultFY]);
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [selectedFY, setSelectedFY] = useState(defaultFY.value || defaultFY.label);
  const [selectedExecutive, setSelectedExecutive] = useState("");
  const [dateRange, setDateRange] = useState([
    new Date(defaultFY.from),
    new Date(defaultFY.to),
  ]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadFilters = useCallback(async () => {
    const filters = await fuziElevatorsApi.fetchDashboardFilters();
    if (filters?.financialYears?.length) {
      setFinancialYears(filters.financialYears);
      const current = filters.financialYears[0];
      setSelectedFY(current.value);
      setDateRange([new Date(current.from), new Date(current.to)]);
    }
    if (filters?.salesExecutives) {
      setSalesExecutives(filters.salesExecutives);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!canView || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const from = formatDate(dateRange[0]);
      const to = formatDate(dateRange[1]);
      const query = buildDashboardQueryParams({
        from,
        to,
        salesExecutive: selectedExecutive,
      });
      const data = await fuziElevatorsApi.fetchFinancialYearDashboard(query);
      setDashboardData(data);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.errors ||
        "Unable to load dashboard data.";
      setError(typeof message === "string" ? message : "Unable to load dashboard data.");
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  }, [canView, dateRange, selectedExecutive]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleFYChange = (value) => {
    setSelectedFY(value);
    const fy = financialYears.find((item) => item.value === value);
    if (fy) setDateRange([new Date(fy.from), new Date(fy.to)]);
  };

  const handleKpiCardClick = (key) => {
    if (!Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]) return;

    const selectedExecutiveOption = salesExecutives.find(
      (item) => item.id === selectedExecutive || item.value === selectedExecutive,
    );
    const baseSearch = (extra = {}) =>
      buildDashboardFilterSearch({
        from: formatDate(dateRange[0]),
        to: formatDate(dateRange[1]),
        financialYear: selectedFY,
        salesExecutive: selectedExecutive,
        salesExecutiveName: selectedExecutiveOption?.label,
        extra,
      });

    const destinations = {
      totalEnquiries: { path: ROUTES.LEAD.LIST, search: baseSearch({ lead_status: "" }) },
      siteVisits: { path: ROUTES.USER_TRACKING, search: baseSearch({ visit_status: "" }) },
      offersCreated: { path: ROUTES.OFFER.LIST, search: baseSearch({ offer_status: "" }) },
      offersSubmitted: { path: ROUTES.OFFER.LIST, search: baseSearch({ offer_status: "Offer Submitted" }) },
      totalOrders: { path: ROUTES.ORDER.LIST, search: baseSearch({ order_status: "" }) },
      ordersCompleted: { path: ROUTES.ORDER.LIST, search: baseSearch({ order_status: "Order Work Complete" }) },
      totalRevenue: { path: ROUTES.ORDER.LIST, search: baseSearch({ order_status: "" }) },
    };

    const destination = destinations[key];
    if (destination) navigate(`${destination.path}${destination.search}`);
  };

  if (!canView) {
    return (
      <Container className="py-4">
        <div className="alert alert-warning mb-0">You do not have permission to view the dashboard.</div>
      </Container>
    );
  }

  const enquiryOverall = (dashboardData?.enquiryStatusOverall || []).map((item) => ({
    label: item.label,
    value: item.value,
    color: item.color,
    percent: item.percent,
  }));

  return (
    <Container fluid className="fy-dashboard py-3">
      <DashboardFilterBar
        financialYears={financialYears}
        salesExecutives={salesExecutives}
        selectedFY={selectedFY}
        selectedExecutive={selectedExecutive}
        dateRange={dateRange}
        onFYChange={handleFYChange}
        onExecutiveChange={setSelectedExecutive}
        onDateRangeChange={setDateRange}
        onRefresh={loadDashboard}
        lastUpdated={dashboardData?.lastUpdated}
      />

      {loading && (
        <div className="fy-loading">
          <Spinner animation="border" size="sm" /> Loading dashboard...
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && dashboardData && (
        <>
          <KpiCardRow kpis={dashboardData.kpis} onCardClick={handleKpiCardClick} />

          <Row className="g-3 mt-1">
            <Col xl={5}>
              <div className="fy-panel">
                <h5 className="fy-panel-title">Revenue Summary (₹)</h5>
                <RevenueSummaryCards revenue={dashboardData.revenue} />
              </div>
            </Col>
            <Col xl={4}>
              <DashboardDonutChart
                title="Revenue by Category (₹)"
                data={dashboardData.revenue?.byCategory || []}
                centerLabel={formatCurrency(dashboardData.revenue?.total, "crore")}
              />
            </Col>
            <Col xl={3}>
              <StatusList
                title="Lift Inventory"
                items={[
                  { label: "Total Lifts Installed", value: dashboardData.liftInventory?.totalInstalled ?? 0 },
                  { label: "New Lifts", value: dashboardData.liftInventory?.newLifts ?? 0 },
                  { label: "Modernization Lifts", value: dashboardData.liftInventory?.modernizationLifts ?? 0 },
                  { label: "AMC Lifts", value: dashboardData.liftInventory?.amcLifts ?? 0 },
                  { label: "Total Lifts", value: dashboardData.liftInventory?.totalLifts ?? 0 },
                ]}
              />
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xl={5}>
              <DashboardTable
                title="Enquiry Status (By Type)"
                columns={ENQUIRY_COLUMNS}
                rows={dashboardData.enquiryStatusByType || []}
              />
            </Col>
            <Col xl={4}>
              <DashboardDonutChart
                title="Enquiry Status Overall"
                data={enquiryOverall}
                centerLabel={dashboardData.kpis?.totalEnquiries?.value ?? 0}
              />
            </Col>
            <Col xl={3}>
              <StatusList
                title="Orders Status"
                items={[
                  { label: "Total Orders", value: dashboardData.orderStatus?.total ?? 0 },
                  { label: "Pending / Working", value: dashboardData.orderStatus?.pendingWorking ?? 0 },
                  { label: "In Progress", value: dashboardData.orderStatus?.inProgress ?? 0 },
                  { label: "Completed", value: dashboardData.orderStatus?.completed ?? 0 },
                  { label: "Cancelled", value: dashboardData.orderStatus?.cancelled ?? 0 },
                ]}
              />
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xl={6}>
              <DashboardLineChart
                title="Monthly Trend Overview"
                monthlyTrends={dashboardData.monthlyTrends}
              />
            </Col>

            <Col xl={3}>
              <div className="fy-panel" style={{ height: "100%" }}>
                <h5 className="fy-panel-title">Order Financial Collection (₹)</h5>
                <DetailedCollectionSummaryCard collectionData={dashboardData.ordersCollection} />
              </div>
            </Col>

            <Col xl={3}>
              <div className="fy-panel" style={{ height: "100%" }}>
                <h5 className="fy-panel-title">AMC Financial Collection (₹)</h5>
                <DetailedCollectionSummaryCard collectionData={dashboardData.amcCollection} />
              </div>
            </Col>
          </Row>

          {/* <Row className="g-3 mt-1">
            <Col xl={12}>
              <DashboardBarChart
                title="Collection Trend (₹)"
                monthly={dashboardData.collections?.monthly || []}
              />
            </Col>
          </Row> */}

          <Row className="g-3 mt-1">
            <Col xl={3}>
              <MetricGrid
                title="AMC Overview"
                items={[
                  { label: "Total AMC Contracts", value: dashboardData.amcOverview?.totalContracts ?? 0 },
                  { label: "Active AMC", value: dashboardData.amcOverview?.active ?? 0 },
                  { label: "Expired AMC", value: dashboardData.amcOverview?.expired ?? 0 },
                  { label: "Pending Renewal", value: dashboardData.amcOverview?.pendingRenewal ?? 0 },
                  { label: "Total AMC Revenue", value: formatCurrency(dashboardData.amcOverview?.revenue) },
                ]}
              />
            </Col>
            <Col xl={3}>
              <MetricGrid
                title="Warranty Overview"
                items={[
                  { label: "Active Warranty", value: dashboardData.warrantyOverview?.active ?? 0 },
                  { label: "Expired Warranty", value: dashboardData.warrantyOverview?.expired ?? 0 },
                  { label: "Expiring in 30 Days", value: dashboardData.warrantyOverview?.expiringIn30Days ?? 0 },
                ]}
              />
            </Col>
            <Col xl={3}>
              <MetricGrid
                title="Lift Status Overview"
                items={[
                  { label: "Installed", value: dashboardData.liftStatus?.installed ?? 0 },
                  { label: "Under Installation", value: dashboardData.liftStatus?.underInstallation ?? 0 },
                  { label: "Pending Installation", value: dashboardData.liftStatus?.pendingInstallation ?? 0 },
                  { label: "Not Started", value: dashboardData.liftStatus?.notStarted ?? 0 },
                ]}
              />
              <ProgressPanel
                title=""
                progressPercent={dashboardData.liftStatus?.progressPercent ?? 0}
                completed={dashboardData.liftStatus?.completedInstallations ?? 0}
                total={dashboardData.liftStatus?.totalInstallations ?? 0}
              />
            </Col>
            <Col xl={3}>
              <QuickSummary data={dashboardData.quickSummary} />
            </Col>
          </Row>

          <p className="fy-footer-note mt-3 mb-0">
            Note: All amounts are in INR | Dashboard data is as per selected Financial Year and filters
          </p>
        </>
      )}
    </Container>
  );
}
